import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client.js';
import { useRefreshSession } from '../auth/useSession.js';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';
import { PasswordChecklist, passwordMeetsPolicy } from '../components/PasswordChecklist.js';

/**
 * Forced password change.
 *
 * Reached when an account is flagged `mustChangePassword` — which the admin
 * seed script always sets, since the password it generates is printed to a
 * terminal and should not survive first use.
 *
 * Changing a password revokes every session, this one included, so the user is
 * returned to the sign-in screen afterwards. That is the point of the
 * revocation, not a rough edge.
 */
export function ChangePasswordPage() {
  const refreshSession = useRefreshSession();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const meetsPolicy = passwordMeetsPolicy(newPassword);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('The two new passwords do not match.');
      return;
    }

    setError(null);
    setBusy(true);

    try {
      await api.post('/api/auth/password', { currentPassword, newPassword });
      await refreshSession();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change the password.');
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-sunken px-4 py-12">
      <div className="w-full max-w-sm">
        <header className="mb-6 text-center">
          <h1 className="text-lg font-semibold tracking-tight text-strong">
            Choose a new password
          </h1>
          <p className="mt-1 text-sm text-muted">
            This account is still using a temporary password.
          </p>
        </header>

        <Card className="p-6">
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            {error && <Alert>{error}</Alert>}

            <Field label="Current password">
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
                autoFocus
                disabled={busy}
              />
            </Field>

            <div>
              <Field label="New password">
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={busy}
                />
              </Field>
              <PasswordChecklist password={newPassword} />
            </div>

            <Field label="Confirm new password">
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                disabled={busy}
              />
            </Field>

            {mismatch && (
              <p className="text-xs text-danger-text">The two passwords do not match.</p>
            )}

            <Button type="submit" className="w-full" disabled={busy || mismatch || !meetsPolicy}>
              {busy ? 'Saving…' : 'Change password'}
            </Button>
          </form>
        </Card>

        <p className="mt-4 text-center text-xs text-faint">
          You will be signed out and asked to sign in again — changing a password ends every
          existing session.
        </p>
      </div>
    </div>
  );
}
